import { useEffect } from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Table } from '@tanstack/react-table';
import ReactPaginate from 'react-paginate';

import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PaginationProps {
  className?: string;
}

export function Pagination<DataType>({
  className,
  table,
}: { table: Table<DataType> } & PaginationProps) {
  const totalCount = table.getRowCount();
  const pageCount = table.getPageCount();

  useEffect(() => {
    table.setPagination({
      pageIndex: 0,
      pageSize: table.getState().pagination.pageSize
    });
  }, [totalCount]);

  return pageCount > 1 ? (
    <div
      className={cn(
        'hidden sm:flex sm:flex-1 mt-4 sm:items-center sm:justify-between border-t-[1px] border-zinc-800 px-[24px] py-[10px]',
        className
      )}
    >
      <p className="text-sm text-zinc-400">{totalCount} row(s)</p>

      <ReactPaginate
        pageCount={pageCount}
        forcePage={table.getState().pagination.pageIndex}
        pageRangeDisplayed={5}
        marginPagesDisplayed={2}
        onPageChange={({ selected }) => table.setPageIndex(selected)}
        breakLabel="..."
        previousLabel={
          <Button
            size="sm"
            variant="transparent"
            className="[&_svg]:size-4"
            data-testid="previous-page"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <CaretLeftIcon />
            Previous
          </Button>
        }
        nextLabel={
          <Button
            size="sm"
            variant="transparent"
            data-testid="next-page"
            className="[&_svg]:size-4"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
            <CaretRightIcon />
          </Button>
        }
        renderOnZeroPageCount={() => null}
        containerClassName="flex items-center gap-2"
        pageLinkClassName={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'w-[40px] h-[40px] border-0'
        )}
        activeClassName={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'w-[40px] h-[40px] overflow-hidden'
        )}
        activeLinkClassName={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'w-[40px] h-[40px]'
        )}
        breakClassName="inline-flex items-center justify-center w-[40px] h-[40px] border-0"
        breakLinkClassName="inline-flex items-center justify-center w-[40px] h-[40px] border-0"
      />
    </div>
  ) : null;
}
